/**
 * Parent-facing "Send a report to the doctor" single drop point.
 *
 * One place for a parent to drop ANY report (NFS, serology, ETF/imaging, other).
 * Pipeline: file picker / drag-and-drop → POST /attachments (kind
 * 'review_inbox', multipart) → POST /intake/submissions { attachmentId, note }.
 * The parent does NOT classify or extract anything — the doctor reviews each
 * submission from the Reports inbox. This component just collects the file plus
 * an optional note, confirms it was sent, and lists the parent's own recent
 * submissions with status badges.
 *
 * App-specific labels that are not in the shared i18n catalog use a tiny inline
 * FR/EN map keyed off the current locale.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getCsrfToken, downloadFile, ApiError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Button, Badge, Field } from './ui.js';
import { ErrorState, QueryState } from './form.js';

// --- Inline bilingual strings (not present in the shared catalog) -----------
const L = {
  drop: { fr: 'Glissez-déposez un compte rendu ici', en: 'Drag & drop a report here' },
  dropOr: { fr: 'ou', en: 'or' },
  dropActive: { fr: 'Déposez pour envoyer', en: 'Drop to send' },
  pick: { fr: 'Choisir un fichier (PDF ou photo)', en: 'Choose a file (PDF or photo)' },
  unsupportedFile: { fr: 'Type de fichier non pris en charge (PDF ou image).', en: 'Unsupported file type (PDF or image).' },
  noteLabel: { fr: 'De quel compte rendu s’agit-il ? (facultatif)', en: 'What is this report? (optional)' },
  notePlaceholder: {
    fr: 'Ex. NFS du 12/05, échographie cérébrale, sérologie…',
    en: 'E.g. CBC from 12 May, brain ultrasound, serology…',
  },
  hint: {
    fr: 'Envoyez n’importe quel compte rendu (NFS, sérologie, imagerie, autre). Le médecin l’examinera.',
    en: 'Send any report (CBC, serology, imaging, other). The doctor will review it.',
  },
  uploading: { fr: 'Envoi…', en: 'Sending…' },
  submit: { fr: 'Envoyer au médecin', en: 'Send to the doctor' },
  cancel: { fr: 'Annuler', en: 'Cancel' },
  sent: { fr: 'Envoyé au médecin pour examen', en: 'Sent to the doctor for review' },
  sendAnother: { fr: 'Envoyer un autre', en: 'Send another' },
  recent: { fr: 'Mes envois récents', en: 'My recent submissions' },
  noneYet: { fr: 'Aucun envoi pour le moment.', en: 'Nothing sent yet.' },
  viewScan: { fr: 'Voir le document', en: 'View scan' },
  statusPending: { fr: 'En attente', en: 'Pending' },
  statusReviewed: { fr: 'Examiné', en: 'Reviewed' },
  statusDismissed: { fr: 'Écarté', en: 'Dismissed' },
} as const;

type Lang = 'fr' | 'en';

interface Submission {
  id: string;
  note: string | null;
  status: 'pending' | 'reviewed' | 'dismissed';
  detectedType: string | null;
  submittedByRole: string;
  resultType: string | null;
  createdAt: string;
  reviewedAt: string | null;
  attachmentId: string;
  filename: string;
  contentType: string;
}

const STATUS: Record<Submission['status'], { tone: 'info' | 'ok' | 'warn'; label: { fr: string; en: string } }> = {
  pending: { tone: 'info', label: L.statusPending },
  reviewed: { tone: 'ok', label: L.statusReviewed },
  dismissed: { tone: 'warn', label: L.statusDismissed },
};

export function ReportUpload({ onDone, className }: { onDone?: () => void; className?: string }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const lang: Lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  // Phases: pick → confirm (file chosen, optional note) → done
  const [file, setFile] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [done, setDone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['intake'],
    queryFn: () => api.get<{ submissions: Submission[] }>('/intake/submissions'),
  });

  function resetAll() {
    setFile(null);
    setNote('');
    setDone(false);
    setDragging(false);
    setDropError(null);
    submitM.reset();
  }

  // Upload the chosen file (kind review_inbox) then create the intake submission.
  const submitM = useMutation({
    mutationFn: async ({ file, note }: { file: File; note: string }) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'review_inbox');
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': getCsrfToken() ?? '' },
        body: fd,
      });
      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;
      if (!res.ok) {
        const err = json?.error ?? {};
        throw new ApiError(res.status, err.code ?? 'upload_failed', err.message ?? res.statusText, err.details);
      }
      const attachmentId: string = json.attachment.id;
      return api.post<{ submission: Submission }>('/intake/submissions', {
        attachmentId,
        note: note.trim() || undefined,
      });
    },
    onSuccess: () => {
      setDone(true);
      void qc.invalidateQueries({ queryKey: ['intake'] });
      onDone?.();
    },
  });

  const downloadM = useMutation({
    mutationFn: (s: Submission) => downloadFile(`/attachments/${s.attachmentId}/download`, s.filename),
  });

  function acceptFile(f: File | undefined | null) {
    if (!f) return;
    const ok = f.type === 'application/pdf' || f.type.startsWith('image/');
    if (!ok) {
      setDropError(tr(L.unsupportedFile));
      return;
    }
    setDropError(null);
    setFile(f);
  }
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) acceptFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  };

  const busy = submitM.isPending;

  return (
    <div className={className}>
      {/* --- Success state --- */}
      {done && (
        <div className="space-y-3">
          <div className="text-sm rounded-lg px-3 py-2 bg-green-50 border border-green-200 text-green-800" role="status">
            ✓ {tr(L.sent)}
          </div>
          <Button type="button" variant="secondary" onClick={resetAll}>
            {tr(L.sendAnother)}
          </Button>
        </div>
      )}

      {/* --- Pick + confirm --- */}
      {!done && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (file) submitM.mutate({ file, note });
          }}
          className="space-y-3"
        >
          <p className="text-sm text-slate-500">{tr(L.hint)}</p>

          {file === null ? (
            <label
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition ${
                dragging ? 'border-brand bg-brand-light/40' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
              }`}
            >
              <span className="text-2xl" aria-hidden>📤</span>
              <span className="text-sm font-medium text-slate-700">{dragging ? tr(L.dropActive) : tr(L.drop)}</span>
              <span className="text-xs text-slate-400">{tr(L.dropOr)}</span>
              <span className="text-sm text-brand underline">{tr(L.pick)}</span>
              <input type="file" accept="application/pdf,image/*" onChange={onFile} className="hidden" />
            </label>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span className="text-lg" aria-hidden>📄</span>
              <span className="flex-1 truncate">{file.name}</span>
              {!busy && (
                <button type="button" className="text-xs underline text-slate-500" onClick={() => setFile(null)}>
                  {tr(L.cancel)}
                </button>
              )}
            </div>
          )}

          {dropError && <p className="text-sm text-danger">{dropError}</p>}

          <Field label={tr(L.noteLabel)}>
            <input
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              value={note}
              placeholder={tr(L.notePlaceholder)}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>

          {submitM.isError && <ErrorState error={submitM.error} fallback={t('common.error')} />}

          <Button type="submit" disabled={busy || file === null}>
            {busy ? tr(L.uploading) : tr(L.submit)}
          </Button>
        </form>
      )}

      {/* --- Parent's own recent submissions --- */}
      <div className="mt-5 border-t border-slate-100 pt-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">{tr(L.recent)}</h3>
        <QueryState
          isLoading={list.isLoading}
          isError={list.isError}
          error={list.error}
          errorFallback={t('common.error')}
          isEmpty={(list.data?.submissions.length ?? 0) === 0}
          emptyMessage={tr(L.noneYet)}
        >
          <ul className="space-y-2">
            {list.data?.submissions.map((s) => {
              const meta = STATUS[s.status];
              return (
                <li key={s.id} className="flex items-start gap-2 text-sm">
                  <Badge tone={meta.tone}>{tr(meta.label)}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-slate-700">{s.note?.trim() || s.filename}</div>
                    <div className="text-xs text-slate-400">{s.createdAt?.slice(0, 10)}</div>
                  </div>
                  <button type="button" className="text-xs underline text-brand whitespace-nowrap" onClick={() => downloadM.mutate(s)}>
                    {tr(L.viewScan)}
                  </button>
                </li>
              );
            })}
          </ul>
        </QueryState>
      </div>
    </div>
  );
}

export { L as reportUploadStrings };
